import React, { useState, Suspense, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Router, Route, Switch, Redirect } from 'react-router-dom';
import { createBrowserHistory } from 'history';
import { SearchBox, LoadingIndicator, RouterTabs } from 'sharedComponents';
import ConnectedAssetsDrives from './ConnectedAssetsDrives/ConnectedAssetsDrivesComponent';
import ConnectedAssetsEnvironmentSensors from './ConnectedAssetsEnvironmentSensors/ConnectedAssetsEnvironmentSensorsComponent';
import { getTabOptions, MAX_SEARCH_LENGTH } from './helpers';
import { assetTypes, gatewayRouteURL } from './constants';

const history = createBrowserHistory();

const ConnectedAssetsHeaderComponent = ({ gatewayInformation, gatewayConnectedAssetsData, handleShowInfoClick, gatewayID, gatewaySerialNumber, isMoveValid, validationLoadingStatus, isSwitchValid, validateSwitchLoadingStatus, gatewayAssetsList, gatewayAssetsListLoadingStatus, isSwitchEgwToAnotherGateway, switchGatewayId, onResetAssetInfo }) => {
    const { t: translate } = useTranslation();
    const [searchValue, setSearchValue] = useState('');
    const [tabOptions, setTabOptions] = useState(getTabOptions(translate));
    const [driveAssets, setDriveAssets] = useState([]);
    const [smartSensorAssets, setSmartSensorAssets] = useState([]);
    const [environmentSensor, setEnvironmentSensor] = useState({});
    const [isSearchBoxAvailable, setIsSearchBoxAvailable] = useState(false);
    const [isEnvironmentSensor, setIsEnvironmentSensor] = useState(null);

    useEffect(() => {
        // Get gateway assets and environment sensor details
        const gatewayAssets = gatewayConnectedAssetsData?.gatewayConnectedAssets?.gatewayAssets || [];
        const environmentSensorDetails = gatewayConnectedAssetsData?.gatewayConnectedAssets?.environmentSensorDetails;

        // Set environment sensor flag
        setIsEnvironmentSensor(gatewayConnectedAssetsData?.gatewayConnectedAssets?.isEnvironmentSensor);

        // Count drive assets (Drive type only) and smart sensor assets (all non-Drive types)
        const gatewayConnectedAssetsCounts = gatewayAssets?.reduce(
            (acc, asset) => {
                if (asset.assetType === assetTypes.DRIVE) {
                    acc.drives += 1;
                } else {
                    acc.smartSensorAssets += 1;
                }
                return acc;
            },
            { drives: 0, smartSensorAssets: 0 }
        );

        // Count environment sensors from environmentSensorDetails
        let envSensorCount = 0;
        if (Array.isArray(environmentSensorDetails)) {
            envSensorCount = environmentSensorDetails.length;
        } else if (environmentSensorDetails && typeof environmentSensorDetails === 'object') {
            envSensorCount = 1;
        }
        // Update tab labels independently
        const getModifiedTabOptions = getTabOptions(translate);
        getModifiedTabOptions[0].title = `${getModifiedTabOptions[0]?.title} (${gatewayConnectedAssetsCounts?.drives})`;
        getModifiedTabOptions[1].title = envSensorCount > 0
            ? `${getModifiedTabOptions[1]?.title} (${envSensorCount})`
            : getModifiedTabOptions[1]?.title;
        setTabOptions(getModifiedTabOptions);

        gatewayConnectedAssetsCounts?.drives === 0 && gatewayConnectedAssetsCounts?.smartSensorAssets === 0 ? setIsSearchBoxAvailable(false) : setIsSearchBoxAvailable(true);

        // Always set environment sensor details
        setEnvironmentSensor(environmentSensorDetails);
    }, [
        translate,
        gatewayConnectedAssetsData?.gatewayConnectedAssets?.gatewayAssets,
        gatewayConnectedAssetsData?.gatewayConnectedAssets?.environmentSensorDetails,
        gatewayConnectedAssetsData?.gatewayConnectedAssets?.isEnvironmentSensor
    ]);

    useEffect(() => {
        if (gatewayConnectedAssetsData?.gatewayConnectedAssets?.gatewayAssets &&
            gatewayConnectedAssetsData?.gatewayConnectedAssets?.gatewayAssets?.length > 0) {
            const gatewayAssetsData = gatewayConnectedAssetsData?.gatewayConnectedAssets?.gatewayAssets;
            const drives = gatewayAssetsData.filter(asset => asset.assetType === assetTypes.DRIVE);
            const environmentSensorDetails = gatewayConnectedAssetsData?.gatewayConnectedAssets?.environmentSensorDetails;
            setDriveAssets(drives);
            setEnvironmentSensor(environmentSensorDetails);
            const smartSensorAssets = gatewayAssetsData.filter(asset => asset.assetType !== assetTypes.DRIVE);
            if (smartSensorAssets.length > 0) {
                setIsEnvironmentSensor(false);
                setSmartSensorAssets(smartSensorAssets);
                setEnvironmentSensor({});
            }
        }
    }, [gatewayConnectedAssetsData?.gatewayConnectedAssets?.gatewayAssets, gatewayConnectedAssetsData?.gatewayConnectedAssets?.environmentSensorDetails]);

    const getBasePath = () => {
        const match = location?.pathname?.match(new RegExp(`^(\\/[^/]+)?\\/${gatewayRouteURL}\\/`));
        return match?.[1] || '';
    };

    const basePath = getBasePath();

    const handleTabChange = (route) => {
        const routeWithoutBasePath = route.replace(basePath, '');
        if (tabOptions[0].route === routeWithoutBasePath) {
            driveAssets.length === 0 && smartSensorAssets.length === 0 ? setIsSearchBoxAvailable(false) : setIsSearchBoxAvailable(true);
        } else if (tabOptions[1].route === routeWithoutBasePath) {
            setIsSearchBoxAvailable(false);
            onResetAssetInfo();
        }
    };

    const handleChangeSearch = (value) => {
        if (value.length <= MAX_SEARCH_LENGTH) {
            setSearchValue(value);
        }
    };

    const sharedDriveProps = {
        gatewayInformation,
        handleShowInfoClick,
        gatewayID,
        gatewaySerialNumber,
        isMoveValid,
        validationLoadingStatus,
        isSwitchValid,
        validateSwitchLoadingStatus,
        gatewayAssetsList,
        gatewayAssetsListLoadingStatus,
        isSwitchEgwToAnotherGateway,
        switchGatewayId,
        onResetAssetInfo,
        searchQuery: searchValue,
    };

    const renderConnectedAssetsContent = () => {
        if (smartSensorAssets.length && !isEnvironmentSensor) {
            return (
                <ConnectedAssetsDrives
                    {...sharedDriveProps}
                    gatewayConnectedDriveData={smartSensorAssets}
                    assetType={assetTypes.SMART_SENSOR}
                />
            );
        }
        if (isEnvironmentSensor) {
            return (
                <Router history={history}>
                    <RouterTabs
                        tabs={tabOptions}
                        variant='toggle'
                        defaultTab={tabOptions[0].route}
                        onChange={handleTabChange}
                        getBasePath={basePath}
                    >
                        <Suspense fallback={<LoadingIndicator />}>
                            <Switch>
                                <Route path={`${basePath}${tabOptions[0].route}`}>
                                    <ConnectedAssetsDrives
                                        {...sharedDriveProps}
                                        gatewayConnectedDriveData={driveAssets}
                                        assetType={assetTypes.DRIVE}
                                    />
                                </Route>
                                <Route path={`${basePath}${tabOptions[1].route}`}>
                                    <ConnectedAssetsEnvironmentSensors gatewayInformation={gatewayInformation} gatewayConnectedEnvironmentSensorData={environmentSensor} gatewayID={gatewayID} />
                                </Route>
                                <Redirect from='*' to={`${basePath}${tabOptions[0].route}`} />
                            </Switch>
                        </Suspense>
                    </RouterTabs>
                </Router>
            );
        }
        return (
            <ConnectedAssetsDrives
                {...sharedDriveProps}
                gatewayConnectedDriveData={driveAssets}
                assetType={assetTypes.DRIVE}
            />
        );
    };

    return (
        <div className='connected-assets__header'>
            <div className='header__search-wrapper'>
                {isSearchBoxAvailable &&
                    <SearchBox
                        className='search-box'
                        placeholder={translate('ABB.Powertrain.Frontend.gatewayManagementSearch')}
                        onChange={handleChangeSearch}
                        value={searchValue}
                    />
                }
            </div>
            <div className={`${!isEnvironmentSensor ? 'header__tab-wrapper--top' : ''} header__tab-wrapper`}>
                {renderConnectedAssetsContent()}
            </div >
        </div >
    );
};

ConnectedAssetsHeaderComponent.propTypes = {
    gatewayConnectedAssetsData: PropTypes.shape({
        gatewayConnectedAssets: PropTypes.shape({
            gatewayAssets: PropTypes.array,
            environmentSensorDetails: PropTypes.oneOfType([
                PropTypes.array,
                PropTypes.object
            ]),
            isEnvironmentSensor: PropTypes.bool
        })
    }),
    gatewayID: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    handleShowInfoClick: PropTypes.func.isRequired,
    gatewaySerialNumber: PropTypes.string,
    isMoveValid: PropTypes.bool,
    validationLoadingStatus: PropTypes.string,
    isSwitchValid: PropTypes.bool,
    validateSwitchLoadingStatus: PropTypes.string,
    gatewayAssetsList: PropTypes.array,
    gatewayAssetsListLoadingStatus: PropTypes.string,
    isSwitchEgwToAnotherGateway: PropTypes.bool,
    switchGatewayId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    onResetAssetInfo: PropTypes.func,
    gatewayInformation: PropTypes.object
};

export default ConnectedAssetsHeaderComponent;
